/**
 * Map of country codes to their validation rules
 * Only includes some common countries for brevity
 */
export const COUNTRY_CODES = {
  "+1": {
    // USA & Canada
    name: "United States/Canada",
    pattern: /^\d{10}$/,
    minLength: 10,
    maxLength: 10,
  },
  "+44": {
    // UK
    name: "United Kingdom",
    pattern: /^[1-9]\d{9,10}$/,
    minLength: 10,
    maxLength: 11,
  },
  "+91": {
    // India
    name: "India",
    pattern: /^[6-9]\d{9}$/,
    minLength: 10,
    maxLength: 10,
  },
  "+86": {
    // China
    name: "China",
    pattern: /^1[3-9]\d{9}$/,
    minLength: 11,
    maxLength: 11,
  },
  "+81": {
    // Japan
    name: "Japan",
    pattern: /^[789]0\d{8}$/,
    minLength: 10,
    maxLength: 10,
  },
  "+49": {
    // Germany
    name: "Germany",
    pattern: /^1[567]\d{8,9}$/,
    minLength: 10,
    maxLength: 11,
  },
  "+33": {
    // France
    name: "France",
    pattern: /^[67]\d{8}$/,
    minLength: 9,
    maxLength: 9,
  },
  "+61": {
    // Australia
    name: "Australia",
    pattern: /^4\d{8}$/,
    minLength: 9,
    maxLength: 9,
  },
  "+65": {
    // Singapore
    name: "Singapore",
    pattern: /^[689]\d{7}$/,
    minLength: 8,
    maxLength: 8,
  },
  "+971": {
    // UAE
    name: "United Arab Emirates",
    pattern: /^5[024568]\d{7}$/,
    minLength: 9,
    maxLength: 9,
  },
};

/**
 * Validates a mobile number for a specific country
 * @param {string} number - The phone number to validate
 * @param {string} countryCode - The country code (e.g., '+91', '+1')
 * @returns {boolean} - Whether the number is valid
 */
export const isValidMobileNumber = (number, countryCode = "+91") => {
  // Remove any non-numeric characters except leading +
  const cleanNumber = number.replace(/[^\d+]/g, "");

  // If country code not found in our mapping, do basic validation
  if (!COUNTRY_CODES[countryCode]) {
    return cleanNumber.length >= 10 && cleanNumber.length <= 15;
  }

  // Get country specific validation rules
  const { pattern } = COUNTRY_CODES[countryCode];

  // Remove country code if present
  const nationalNumber = cleanNumber.startsWith(countryCode)
    ? cleanNumber.slice(countryCode.length)
    : cleanNumber;

  return pattern.test(nationalNumber);
};

/**
 * Validates specifically an Indian mobile number
 * @param {string} number - The phone number to validate
 * @returns {boolean} - Whether the number is valid
 */
export const isValidIndianMobile = (number) => {
  return isValidMobileNumber(number, "+91");
};

/**
 * Formats a phone number with the appropriate country code
 * @param {string} number - The phone number to format
 * @param {string} countryCode - The country code (e.g., '+91', '+1')
 * @returns {string} - The formatted number with country code
 */
export const formatMobileNumber = (number, countryCode = "+91") => {
  // Remove any non-numeric characters except leading +
  const cleanNumber = number.replace(/[^\d+]/g, "");

  // If the number already has a country code, return as is
  if (cleanNumber.startsWith("+")) {
    return cleanNumber;
  }

  // Remove any leading zeros
  const nationalNumber = cleanNumber.replace(/^0+/, "");

  // If the number is not valid for the country code, return original
  if (!isValidMobileNumber(nationalNumber, countryCode)) {
    return number;
  }

  // Add country code
  return `${countryCode}${nationalNumber}`;
};

/**
 * Formats a phone number specifically for India
 * @param {string} number - The phone number to format
 * @returns {string} - The formatted number with Indian country code
 */
export const formatIndianMobile = (number) => {
  return formatMobileNumber(number, "+91");
};

/**
 * Gets the maximum allowed length for a phone number for a specific country
 * @param {string} countryCode - The country code (e.g., '+91', '+1')
 * @returns {number} - The maximum allowed length
 */
export const getMaxLengthForCountry = (countryCode) => {
  if (!COUNTRY_CODES[countryCode]) {
    return 15; // Default max length per ITU-T recommendation
  }
  return COUNTRY_CODES[countryCode].maxLength;
};

/**
 * Gets a list of all supported country codes with their names
 * @returns {Array<{code: string, name: string}>} - List of country codes and names
 */
export const getSupportedCountries = () => {
  return Object.entries(COUNTRY_CODES).map(([code, info]) => ({
    code,
    name: info.name,
  }));
};
